/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import template from './notification-preferences-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

interface NotificationMessageConfig {
  id: string;
  sev: string;
}

@Component({
  standalone: false,
  selector: 'adn-notification-preferences-edit-page',
  template
})
export class NotificationPreferencesEditPageComponent extends AdminResourceEditPageBase {
  readonly notificationLevels = ['INFO', 'WARNING', 'IMPORTANT', 'ERROR'];
  readonly notificationObjects = ['LINE_ITEM', 'CREATIVE', 'REPORT_EXECUTION', 'DATA_EXPORT'];
  readonly notificationMethods = ['EMAIL', 'UI'];
  readonly notificationMessages: NotificationMessageConfig[];

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.notificationMessages = [
      {id: 'LINE_ITEM_READY', sev: 'INFO'},
      {id: 'LINE_ITEM_RUNNING', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_STOPPED_RUNNING', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_ENDED_END_DATE_PASSED', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_ENDED_OBJECTIVE_REACHED', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_NOT_STARTED', sev: 'WARNING'},
      {id: 'LINE_ITEM_DELETED', sev: 'INFO'},
      {id: 'LINE_ITEM_SUBMITTED', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_REJECTED', sev: 'IMPORTANT'},
      {id: 'CREATIVE_REJECTED', sev: 'IMPORTANT'},
      {id: 'REPORT_COMPLETE', sev: 'INFO'},
      {id: 'DATA_EXPORT_HAS_FAILED', sev: 'ERROR'}
    ];

    if (LocalNetworkProfile.isSuiPlatform()) {
      this.notificationMessages.push({id: 'NEW_SIGNUP_USER', sev: 'INFO'});
    }
  }

  /** Returns (and lazily initialises) the overrides array for a message id. */
  getOverrides(messageId: string): string[] {
    this.editingModel.overrides = this.editingModel.overrides || {};
    this.editingModel.overrides[messageId] = this.editingModel.overrides[messageId] || [];
    return this.editingModel.overrides[messageId];
  }

  // ── Validation ───────────────────────────────────────────────────────────────

  hasMethodValidationError(objectType: string): boolean {
    const subscription = this.getSubscription(objectType);
    return subscription.subscribed === true && subscription.deliveryMethods.length === 0;
  }

  hasSeverityValidationError(objectType: string): boolean {
    const subscription = this.getSubscription(objectType);
    return subscription.subscribed === true && !subscription.severityLevel;
  }

  hasAnySubscriptionValidationError(): boolean {
    return this.notificationObjects.some(
      (objectType) => this.hasMethodValidationError(objectType) || this.hasSeverityValidationError(objectType)
    );
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  protected afterInitEditingModel(): void {
    this.initModel();
  }

  protected afterInitAfterSave(): void {
    this.initModel();
  }

  protected onBeforeSubmit(): void {
    this.editingModel.user = {id: this.localUserProfile.get('userId')};

    // Unsubscribed types must be omitted: core requires severityLevel on every
    // remaining subscription entry (empty deliveryMethods is not enough).
    this.notificationObjects.forEach((objectType) => {
      const subscription = this.getSubscription(objectType);
      if (!subscription.subscribed) {
        delete this.editingModel.subscriptions[objectType];
        return;
      }
      delete subscription.editing;
    });
  }

  protected syncEditingModelToApiModel(): void {
    super.syncEditingModelToApiModel();
    // savePartial uses this.model; keep a detached copy so restoring UI shells
    // on editingModel does not put unsubscribed keys back into the POST body.
    this.model.subscriptions = Object.assign({}, this.editingModel.subscriptions || {});
    this.initModel();
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private initModel(): void {
    this.editingModel.subscriptions = this.editingModel.subscriptions || {};
    this.editingModel.overrides = this.editingModel.overrides || {};

    this.notificationObjects.forEach((objectType) => {
      const subscription = this.getSubscription(objectType);
      subscription.subscribed = subscription.deliveryMethods.length > 0;
    });

    this.editingModel.user = {id: this.localUserProfile.get('userId')};
  }

  private getSubscription(objectType: string): any {
    this.editingModel.subscriptions = this.editingModel.subscriptions || {};
    this.editingModel.subscriptions[objectType] = this.editingModel.subscriptions[objectType] || {};
    this.editingModel.subscriptions[objectType].deliveryMethods =
      this.editingModel.subscriptions[objectType].deliveryMethods || [];
    return this.editingModel.subscriptions[objectType];
  }
}
